import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageSquare, MapPin, Clock, Send, Loader2, Plane } from "lucide-react";
import { Reveal, MaskReveal } from "./Reveal";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { PHONE_DISPLAY, PHONE_TEL, SERVICES } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", email: "", phone: "", service: "",
  pickup: "", dropoff: "", ride_date: "", ride_time: "",
  flight_number: "", station_info: "", needs_assistance: false,
  message: "", company: "",
};

const field =
  "bg-black border-white/15 text-white placeholder:text-neutral-600 focus-visible:ring-[#D4AF37] focus-visible:ring-2 focus-visible:border-[#D4AF37] h-12 rounded-lg";

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.pickup || !form.dropoff || !form.ride_date || !form.ride_time) {
      toast.error("Please fill in your name, email, phone, pickup, drop-off, date and time.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address so we can confirm your ride.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form, { timeout: 15000 });
      toast.success("Request sent! We've emailed you a confirmation — please check your inbox (and your spam/junk folder, just in case). Rui will reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call or text us at " + PHONE_DISPLAY + ".");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Info column */}
          <div className="lg:col-span-5">
            <span className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold mb-5">
              <span className="h-px w-10 bg-[#D4AF37]" /> Book Your Ride
            </span>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.03]">
              <MaskReveal lines={["Let's get you", "moving."]} />
            </h2>
            <Reveal delay={0.2}>
              <p className="text-neutral-400 mt-6 leading-relaxed max-w-md">
                Call or text for the fastest response, or send a request below and we'll confirm your ride personally.
              </p>
            </Reveal>

            <div className="mt-10 space-y-4">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="contact-call-button"
                className="group flex items-center gap-4 rounded-xl border border-white/10 bg-[#111111] p-5 hover:border-[#D4AF37]/40 transition-colors duration-300"
              >
                <div className="h-12 w-12 rounded-full bg-[#D4AF37] text-black flex items-center justify-center">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Call or Text</div>
                  <div className="text-white text-xl font-serif-display">{PHONE_DISPLAY}</div>
                </div>
              </a>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/10 bg-[#111111] p-5">
                  <MapPin size={20} className="text-[#D4AF37]" />
                  <div className="text-white font-medium mt-3">Service Area</div>
                  <div className="text-neutral-500 text-sm mt-1">Haymarket, VA & surrounding communities</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-[#111111] p-5">
                  <Clock size={20} className="text-[#D4AF37]" />
                  <div className="text-white font-medium mt-3">Availability</div>
                  <div className="text-neutral-500 text-sm mt-1">Early mornings, evenings & by appointment</div>
                </div>
              </div>

              <a
                href={`sms:${PHONE_TEL}`}
                data-testid="contact-text-button"
                className="flex items-center justify-center gap-2 rounded-xl border border-white/15 text-white p-4 hover:bg-white/5 hover:border-[#D4AF37] transition-colors duration-300"
              >
                <MessageSquare size={18} /> Prefer to text? Tap here
              </a>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-7">
            <Reveal className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-7 md:p-10">
              <form onSubmit={submit} data-testid="booking-form" noValidate className="space-y-5">
                {/* Honeypot (hidden from humans) */}
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={set("company")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-sm">Full Name</Label>
                    <Input data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="John Smith" className={field} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-sm">Phone Number</Label>
                    <Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} placeholder="703-000-0000" className={field} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-neutral-300 text-sm">Email Address</Label>
                  <Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={field} />
                </div>

                <div className="space-y-2">
                  <Label className="text-neutral-300 text-sm">Service Needed</Label>
                  <select
                    data-testid="booking-service"
                    value={form.service}
                    onChange={set("service")}
                    className="w-full h-12 rounded-lg bg-black border border-white/15 text-white px-3 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-[#D4AF37]"
                  >
                    <option value="">Select a service (optional)</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title}>{s.title}</option>
                    ))}
                    <option value="Other / Add-on">Other / Add-on</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-sm">Pickup Location</Label>
                    <Input data-testid="booking-pickup" value={form.pickup} onChange={set("pickup")} placeholder="123 Main St, Haymarket" className={field} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-sm">Drop-off Location</Label>
                    <Input data-testid="booking-dropoff" value={form.dropoff} onChange={set("dropoff")} placeholder="Dulles Airport" className={field} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-sm">Date Needed</Label>
                    <Input
                      data-testid="booking-date"
                      type="date"
                      value={form.ride_date}
                      onChange={set("ride_date")}
                      className={`${field} [color-scheme:dark] cursor-pointer`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-neutral-300 text-sm">Time Needed</Label>
                    <Input
                      data-testid="booking-time"
                      type="time"
                      value={form.ride_time}
                      onChange={set("ride_time")}
                      className={`${field} [color-scheme:dark] cursor-pointer`}
                    />
                  </div>
                </div>

                <div className="rounded-xl border border-white/10 bg-black/40 p-5 space-y-4">
                  <div className="flex items-center gap-2 text-[#D4AF37]">
                    <Plane size={16} />
                    <span className="text-sm font-medium">Airport or train pickup? <span className="text-neutral-500 font-normal">(optional)</span></span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label className="text-neutral-300 text-sm">Flight Number</Label>
                      <Input data-testid="booking-flight" value={form.flight_number} onChange={set("flight_number")} placeholder="e.g. UA1234" className={field} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-neutral-300 text-sm">Train Station / Details</Label>
                      <Input data-testid="booking-station" value={form.station_info} onChange={set("station_info")} placeholder="e.g. Amtrak, Union Station" className={field} />
                    </div>
                  </div>
                  <p className="text-neutral-600 text-xs">Add your flight number so we can track your arrival, or your train/station details for a smooth pickup.</p>
                </div>

                <label
                  data-testid="booking-assistance"
                  className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/40 p-4 cursor-pointer hover:border-[#D4AF37]/40 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={form.needs_assistance}
                    onChange={(e) => setForm((f) => ({ ...f, needs_assistance: e.target.checked }))}
                    className="mt-0.5 h-5 w-5 shrink-0 accent-[#D4AF37] cursor-pointer"
                  />
                  <span>
                    <span className="block text-white text-sm font-medium">I need extra assistance</span>
                    <span className="block text-neutral-500 text-xs mt-0.5">Help with mobility, luggage, wheelchair, or getting in and out of the vehicle.</span>
                  </span>
                </label>

                <div className="space-y-2">
                  <Label className="text-neutral-300 text-sm">Anything else? (optional)</Label>
                  <Textarea
                    data-testid="booking-message"
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Number of passengers, luggage, special requests..."
                    className="bg-black border-white/15 text-white placeholder:text-neutral-600 focus-visible:ring-[#D4AF37] focus-visible:ring-2 focus-visible:border-[#D4AF37] rounded-lg min-h-[110px]"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#D4AF37] text-black px-8 py-4 font-semibold hover:bg-[#f3e5ab] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {loading ? "Sending..." : "Request My Ride"}
                </button>
                <p className="text-center text-neutral-600 text-xs">We'll never share your info. Rui confirms every ride personally.</p>
                <p className="text-center text-[#D4AF37]/80 text-xs" data-testid="email-spam-note">
                  📧 We'll email you a confirmation right away — if you don't see it, please check your <span className="font-semibold">spam / junk</span> folder and mark it "Not spam."
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
